import React from 'react';
import {
    Card, CardContent, Box, Typography, CircularProgress, Chip, 
    List, ListItem, ListItemText, Divider, Button, useTheme, alpha, Grid
} from '@mui/material';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import InfoIcon from '@mui/icons-material/Info';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const SubscriptionPanel = ({ 
    subscriptionPlan, 
    usageData,
    usageLoading,
    subscriptionInfoLoading, 
    onUpgradeClick, 
    user 
}) => {
    const theme = useTheme();

    const formatFeatureValue = (value, type) => {
        if (value === null || value === undefined) {
            return 'N/A';
        }
        
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        
        if (type === 'plates' && value > 0) {
            return `${value} plates`;
        }
        
        if (type === 'keys' && value > 0) {
            return `${value} keys`;
        }
        
        if (value === 0 && (type === 'plates' || type === 'keys')) {
            return 'No';
        }
        
        return value.toString();
    };

    const formatUsageValue = (current, limit) => {
        if (usageLoading) {
            return <CircularProgress size={14} sx={{ color: 'text.secondary' }} />;
        }
        
        if (!usageData || current === null || current === undefined) {
            return limit;
        }
        
        const limitDisplay = limit;
        return `${current} / ${limitDisplay}`;
    };

    const formatBillingAmount = (amount, currency) => {
        return `${currency} ${(amount / 100).toFixed(2)}`;
    };

    const formatNextBillingDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    // Compact feature display component
    const FeatureRow = ({ label, value, description }) => (
        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            py: 0.5,
            '&:not(:last-child)': {
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
            }
        }}>
            <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="500" sx={{ lineHeight: 1.3 }}>
                    {label}
                </Typography>
                {description && (
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                        {description}
                    </Typography>
                )}
            </Box>
            {React.isValidElement(value) ? (
                <Box sx={{ minWidth: 60, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {value}
                </Box>
            ) : (
                <Chip 
                    label={value} 
                    size="small" 
                    variant="outlined"
                    sx={{ 
                        minWidth: 60,
                        height: 24,
                        '& .MuiChip-label': {
                            px: 1,
                            fontSize: '0.75rem'
                        }
                    }}
                />
            )}
        </Box>
    );

    return (
        <Card 
            sx={{ 
                borderRadius: 2, 
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
            data-intro='Here are the details of your account subscription (not specific to any one device). Click Upgrade to gain access to more advanced features of Entrinsic Connect.'
        >
            <Box sx={{ 
                p: 2, 
                bgcolor: theme.palette.primary.main, 
                color: 'white',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 1
            }}>
                <InfoIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold">
                    SUBSCRIPTION PLAN
                </Typography>
            </Box>
            
            <CardContent sx={{ p: 2, flexGrow: 1 }}>
                {subscriptionInfoLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '150px', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>Loading...</Typography>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <>
                        {/* Plan Name */}
                        <Box sx={{ mb: 2 }}>
                            <Chip 
                                label={subscriptionPlan.plan.name}
                                sx={{ 
                                    width: '100%', 
                                    py: 1,
                                    height: 'auto',
                                    fontWeight: 'bold',
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    color: theme.palette.primary.main,
                                    '& .MuiChip-label': {
                                        py: 0.5,
                                        fontSize: '0.9rem'
                                    }
                                }}
                            />
                        </Box>

                        {/* Core Features - Most Important */}
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1, color: 'primary.main' }}>
                            Core Limits
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <FeatureRow 
                                label="Kiosks" 
                                value={formatUsageValue(
                                    usageData?.numberOfKiosks, 
                                    subscriptionPlan.plan.NumberOfAllowedKiosks
                                )} 
                            />
                            <FeatureRow 
                                label="Responders" 
                                value={formatUsageValue(
                                    usageData?.numberOfResponders, 
                                    subscriptionPlan.plan.NumberOfAllowedResponders
                                )} 
                            />
                            <FeatureRow 
                                label="Aliases" 
                                value={formatUsageValue(
                                    usageData?.numberOfAliases, 
                                    subscriptionPlan.plan.NumberOfAllowedAliases
                                )} 
                            />
                            <FeatureRow 
                                label="ANPR Plates" 
                                value={formatUsageValue(
                                    usageData?.ANPRAllowedPlates, 
                                    subscriptionPlan.plan.ANPRAllowedPlates
                                )} 
                            />
                            <FeatureRow 
                                label="QR Code Keys" 
                                value={formatUsageValue(
                                    usageData?.QRCodeKeysAllowed, 
                                    subscriptionPlan.plan.QRCodeKeysAllowed
                                )} 
                            />
                        </Box>

                        {/* Features Grid - More Compact */}
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1, color: 'primary.main' }}>
                            Features
                        </Typography>
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                            <Grid item xs={6}>
                                <Box sx={{ p: 1, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">Movement Videos</Typography>
                                    <Typography variant="body2" fontWeight="500">
                                        {formatFeatureValue(subscriptionPlan.plan.MovementVideoAllowed)}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ p: 1, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">High Resolution</Typography>
                                    <Typography variant="body2" fontWeight="500">
                                        {formatFeatureValue(subscriptionPlan.plan.HighResAllowed)}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ p: 1, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">Gate Opening</Typography>
                                    <Typography variant="body2" fontWeight="500">
                                        {formatFeatureValue(subscriptionPlan.plan.GateOpening)}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ p: 1, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">API Access</Typography>
                                    <Typography variant="body2" fontWeight="500">
                                        {formatFeatureValue(subscriptionPlan.plan.APIAccess)}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Storage - Compact */}
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1, color: 'primary.main' }}>
                            Storage
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <FeatureRow 
                                label="Movement Data" 
                                value={`${subscriptionPlan.plan.MovementDetectionDaysStorage}d`} 
                            />
                            <FeatureRow 
                                label="Activity Logs" 
                                value={`${subscriptionPlan.plan.LogDaysStorage}d`} 
                            />
                        </Box>

                        {/* Billing Information - Only if exists */}
                        {subscriptionPlan.paddleData.items && (
                            <>
                                <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AttachMoneyIcon fontSize="small" />
                                    Billing
                                </Typography>
                                <Box sx={{ 
                                    p: 1.5, 
                                    borderRadius: 1, 
                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                    mb: 2
                                }}>
                                    <Typography variant="body2" fontWeight="500" color="success.dark">
                                        {formatBillingAmount(
                                            subscriptionPlan.paddleData.recurring_transaction_details.totals.total,
                                            subscriptionPlan.paddleData.recurring_transaction_details.totals.currency_code
                                        )}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Next: {formatNextBillingDate(subscriptionPlan.paddleData.items[0]?.next_billed_at)}
                                    </Typography>
                                </Box>
                            </>
                        )}

                        {/* Action Button */}
                        {!user.is_partner_user ? (
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={onUpgradeClick} 
                                startIcon={<UpgradeIcon />}
                                fullWidth
                                size="small"
                                sx={{ 
                                    py: 1,
                                    fontWeight: 'bold',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    '&:hover': {
                                        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                                    }
                                }}
                            >
                                {subscriptionPlan.paddleData.items ? 'CHANGE PLAN' : 'UPGRADE'}
                            </Button>
                        ) : (
                            <Typography 
                                variant='caption' 
                                sx={{ 
                                    p: 1.5, 
                                    borderRadius: 1, 
                                    bgcolor: alpha(theme.palette.info.light, 0.1),
                                    color: theme.palette.info.dark,
                                    textAlign: 'center',
                                    display: 'block'
                                }}
                            >
                                Only your customer can change their subscription level.
                            </Typography>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default SubscriptionPanel; 